/**
 * `stepMoveWords.ts` — did what the learner last said ASK for a step move?
 *
 * The word half of the step-move guardrail (`AIGuardrailsModel.StepMove.ts`,
 * AI-1412). Pure and headless: text in, verdict out.
 *
 * ⚠ **A NOISE FILTER, not an intent classifier.** The Guide still decides what
 * the learner meant and which move that is; this only asks whether they said
 * anything that COULD mean one. So it is generous about wording and strict
 * about one thing: a turn with no move words, no step or button words and no
 * answer to a move question asks for nothing. That is what background noise
 * looks like once the transcriber has made text of it ("Hm.", "Goodbye.",
 * "Hello, how are you?" in the 2026-09-23 traces), and it is what the realtime
 * model moved the run on, because it answers the AUDIO and hears a confident
 * phrase in a muffled sound. No prompt rule reached that (see
 * `docs/work-plan/M14-roles-and-ai-2.md`, "A goodbye is confirmed").
 *
 * Why a button's words and not its whole LABEL: a spoken answer rarely repeats
 * the label. "No drones around here." answers a step whose buttons are "Area
 * is clear" / "Drones are active" — it shares no word with the button it means
 * and one with the button it does not. Choosing between them is the model's
 * job over the labels (`WorkflowExecutor.pressWorkflowTrigger`); here any
 * button word, or a yes/no, shows the learner answered something.
 */

/** Which kind of move the Guide asked for. A replay is not one — it is never checked. */
export type StepMoveKind = "next" | "back" | "step" | "trigger";

/** What the step being moved to, and the step being left, are called. */
export interface StepMoveVocabulary {
  /** The step (or steps) this move can land on — a learner may ask for it by name. */
  readonly stepNames: readonly string[];
  /** The current step's TRIGGER button labels. Read for `trigger` only. */
  readonly buttonLabels: readonly string[];
  /**
   * The label of the learner's own button that makes THIS move: Next's for
   * `next`, Back's for going back (AI-1418). The author writes it, so it can
   * be any word ("Start", "Begin", "Got it"), and it is the one on the screen
   * they are reading — a learner who says it has pressed it out loud. No
   * word list can anticipate it, which is why it is passed in rather than
   * listed.
   */
  readonly moveLabels: readonly string[];
}

/**
 * What the learner's turn is ANSWERING, read from Spark's last line before it
 * (a step's narration included).
 */
export interface StepMoveTurnContext {
  /**
   * That line asked whether to move on — or asked them to confirm something
   * before moving on ({@link isConfirmRequest}) — so a bare "yes" asks.
   */
  readonly answersMoveQuestion: boolean;
  /**
   * That line asked whether they have any questions ("Any questions before we
   * get started?"), so "no" / "no questions" asks to move on (AI-1418, option
   * A). A "yes" there does NOT: it means they have one (`roles/guide.md` → "A
   * bare yes or no is not a move-on reply").
   */
  readonly answersQuestionsQuestion: boolean;
}

/** Why a turn counted as asking. Logged, so a surprising pass can be traced to its rule. */
export type StepMoveAskedBecause =
  | "move_words"
  | "step_name"
  | "button_label"
  | "button_words"
  | "answer_words"
  | "no_questions"
  | "confirmation";

export type StepMoveWordsVerdict =
  | { readonly asked: true; readonly because: StepMoveAskedBecause }
  | {
      readonly asked: false;
      /** Nothing but sounds ("hm", "uh") or nothing at all — no words to answer. */
      readonly pureNoise: boolean;
    };

/**
 * Words and phrases that ask to move, in the tokenized form `tokenize`
 * produces (lower case, apostrophes dropped: "let's" → "lets").
 *
 * The ticket's list ("next", "back", "go to step", "done, ready, or want to
 * move on") plus the move-on replies `roles/guide.md` teaches. Broad on
 * purpose — see the header.
 */
const K_MOVE_PHRASES: readonly string[] = [
  "next",
  "continue",
  "proceed",
  "skip",
  "forward",
  "onward",
  "onwards",
  "advance",
  "back",
  "previous",
  "return",
  "done",
  "ready",
  "finish",
  "finished",
  "complete",
  "completed",
  "move on",
  "moving on",
  "go on",
  "keep going",
  "carry on",
  "go ahead",
  "lets go",
  "go to",
  "take me",
  "start",
  "begin",
  "start over",
  "lets start",
  "lets begin",
  "get started",
  "all set",
  "lets do it",
  "lets do this",
];

/** "step 3", "step three", "the third step", "the last one". */
const K_STEP_REFERENCE =
  / step (?:\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve) | (?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last) (?:step|one) /;

/**
 * The narrower set that makes one of SPARK's questions a question about
 * moving, so a bare "yes" can answer it.
 *
 * ⚠ Narrower than {@link K_MOVE_PHRASES} for a reason: "Any questions before we
 * get started?" must not qualify. A "yes" there means they HAVE a question
 * (`roles/guide.md` → "A bare yes or no is not a move-on reply"), so "get
 * started" belongs on the learner's side only.
 */
const K_MOVE_QUESTION_PHRASES: readonly string[] = [
  "move on",
  "moving on",
  "continue",
  "next",
  "ready",
  "go ahead",
  "carry on",
  "keep going",
  "proceed",
  "go back",
  "skip",
  "take you",
  "start",
  "begin",
];

const K_AFFIRMATIVE_PHRASES: readonly string[] = [
  "yes",
  "yeah",
  "yea",
  "yep",
  "yup",
  "ya",
  "sure",
  "ok",
  "okay",
  "alright",
  "all right",
  "absolutely",
  "definitely",
  "certainly",
  "of course",
  "correct",
  "right",
  "please",
  "go for it",
  "do it",
  "sounds good",
  "mhm",
  "uh huh",
  "affirmative",
];

const K_NEGATIVE_PHRASES: readonly string[] = [
  "no",
  "nope",
  "nah",
  "not",
  "none",
  "nothing",
  "never",
  "nobody",
  "negative",
  "dont",
  "isnt",
  "arent",
  "wasnt",
];

/**
 * "No questions" in any of its forms — what a learner says to "Any questions
 * before we get started?" when they are ready (AI-1418, option A). Read ONLY
 * after such a question (`answersQuestionsQuestion`), never on its own.
 *
 * A noise filter like the rest of this file: "No, actually I do have one"
 * passes it too, and it is the Guide, which reads the whole sentence, that
 * does not move on it.
 */
const K_NO_QUESTIONS_PHRASES: readonly string[] = [
  "no",
  "nope",
  "nah",
  "none",
  "nothing",
  "not really",
  "im good",
  "i am good",
  "im fine",
  "all good",
  "were good",
  "good to go",
];

/**
 * A question asking whether the learner HAS questions — "any questions",
 * "do you have questions", "more questions". Matched on the last question in
 * Spark's line, like {@link isMoveQuestion}.
 */
const K_QUESTIONS_QUESTION = / (?:any|have|more|other|further) (?:\w+ )?questions? /;

/**
 * Words too common to show a turn is ABOUT a step or a button. A step called
 * "Check the area" must not be matched by every sentence containing "the".
 */
const K_STOPWORDS = new Set<string>([
  "the", "and", "are", "was", "were", "for", "with", "this", "that", "these", "those",
  "there", "here", "then", "than", "what", "which", "who", "whom", "how", "why", "when",
  "where", "you", "your", "yours", "our", "ours", "they", "them", "their", "its", "his",
  "her", "him", "she", "has", "have", "had", "can", "could", "would", "should", "will",
  "shall", "may", "might", "must", "just", "also", "too", "very", "really", "now", "all",
  "any", "some", "one", "get", "got", "let", "lets", "from", "into", "onto", "about",
  "over", "under", "off", "out", "step", "steps", "ive", "thats", "whats", "please",
  "okay", "yes", "yeah", "not", "dont",
]);

/**
 * A sound rather than a word: "hm", "hmm", "mm", "mhm", "uh", "um", "er",
 * "erm", "ah", "oh", "eh", "huh". Repeated letters included ("hmmm", "uhhh").
 */
const K_FILLER_TOKEN = /^(?:h*m+|h+m*|m+h+m+|u+[hm]*|e+r+m*|a+h+|o+h*|e+h+|h+u+h+)$/;

/** Lower case, apostrophes dropped ("let's" → "lets"), everything else a space. */
export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[‘’`']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter((token) => token.length > 0);
}

/**
 * Whether one of Spark's own lines asked the learner whether to move on. Reads
 * the LAST question in the line only: a "yes" answers the last thing asked,
 * and "Ready to move on? Or is there anything else?" is not asking to move. A
 * bare "Yes or no?" after it is not a question of its own (see `lastQuestion`).
 *
 * `moveLabels` are the step's own button labels (AI-1418): a question that
 * names the button — "Ready to start?" on a step whose Next reads "Start" — is
 * a question about pressing it, whatever the author called it.
 */
export function isMoveQuestion(line: string, moveLabels: readonly string[] = []): boolean {
  const question = lastQuestion(line);
  if (question === null) return false;
  const tokens = tokenize(question);
  return containsAny(tokens, K_MOVE_QUESTION_PHRASES) || containsAny(tokens, labelPhrases(moveLabels));
}

/**
 * Whether one of Spark's own lines asked whether the learner has any
 * questions — "Any questions before we get started?". Its last question only,
 * like {@link isMoveQuestion}.
 */
export function isQuestionsQuestion(line: string): boolean {
  const question = lastQuestion(line);
  return question !== null && K_QUESTIONS_QUESTION.test(` ${tokenize(question).join(" ")} `);
}

/**
 * Asking the learner to CONFIRM something before moving on —
 * "Confirm the overhead work area is clear before proceeding." A step's
 * narration asks this way as often as with a question mark, and a "yes" to it
 * is a "yes" to moving on, so it is read like {@link isMoveQuestion}
 * (2026-10-01 trace: "Yes, the area is clear." was refused as `not_asked`
 * because the line has no "?"). Two parts must both be in the line's LAST
 * sentence, in either order: a confirming verb, and "before" + a move ("before
 * proceeding", "before you continue", "before pressing" — the button's own
 * label can be any words, so the press is matched rather than the label).
 *
 * ⚠ Deliberately narrow, user decision 2026-10-01: only an AFFIRMATIVE answers
 * it (the `confirmation` rule). A reply that merely shares a word with the
 * thing to confirm ("Looks pretty clear.") is NOT accepted — speaker echo of
 * the narration would share every word — so Spark first asks "Ready to move
 * on?", as for any other refused move (`WorkflowExecutor.refuseUnaskedMoveAsync`).
 */
export function isConfirmRequest(line: string): boolean {
  const sentence = lastSentence(line);
  if (sentence === null) return false;
  const padded = ` ${tokenize(sentence).join(" ")} `;
  return K_CONFIRM_VERB.test(padded) && K_BEFORE_MOVING.test(padded);
}

/** "confirm", "make sure", "check", "verify", "ensure" — in tokenized form. */
const K_CONFIRM_VERB = / (?:confirm|make sure|check|verify|ensure) /;

/**
 * "before" followed by a move, with an optional "you" / "we": "before
 * proceeding", "before we move on", "before you press". Tokenized form.
 */
const K_BEFORE_MOVING =
  / before (?:(?:you|we) )?(?:proceed|proceeding|continue|continuing|move on|moving on|go on|going on|carry on|carrying on|go ahead|going ahead|start|starting|begin|beginning|press|pressing|click|clicking|tap|tapping) /;

/** The line's last sentence, whatever ends it. */
function lastSentence(line: string): string | null {
  const sentences = line
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.length > 0);
  return sentences.length > 0 ? sentences[sentences.length - 1]! : null;
}

/** Whether the line ENDS on a question, whatever it asks. */
export function endsWithQuestion(line: string): boolean {
  return line.trim().endsWith("?");
}

/**
 * Read one learner turn against one move.
 *
 * `context.answersMoveQuestion` is true when the turn answers a question about
 * moving — Spark's last line asked one, or the guardrail refused a move and
 * told Spark to ask. Then a bare "yes" is the learner asking, and it is checked
 * FIRST so a "mhm" counts as the yes it is rather than as a sound.
 *
 * `context.answersQuestionsQuestion` is true when that line asked whether they
 * have any questions. Then "no" / "no questions" asks to move on — for a
 * `next` only, since that question is what stands between them and Next.
 *
 * A button press (`trigger`) needs an ANSWER, not a move word: "keep going"
 * says nothing about what the step asked, which is `roles/guide.md`'s own rule
 * for trigger steps ("A reply that only asks to MOVE ON answers nothing, so it
 * presses nothing").
 */
export function readStepMoveWords(
  said: string,
  kind: StepMoveKind,
  vocabulary: StepMoveVocabulary,
  context: StepMoveTurnContext,
): StepMoveWordsVerdict {
  const tokens = tokenize(said);

  if (context.answersMoveQuestion && containsAny(tokens, K_AFFIRMATIVE_PHRASES)) {
    return { asked: true, because: "confirmation" };
  }
  if (
    kind === "next" &&
    context.answersQuestionsQuestion &&
    containsAny(tokens, K_NO_QUESTIONS_PHRASES)
  ) {
    return { asked: true, because: "no_questions" };
  }
  if (tokens.every((token) => K_FILLER_TOKEN.test(token))) {
    return { asked: false, pureNoise: true };
  }

  const content = contentWords(tokens);
  if (saysLabel(tokens, content, vocabulary.moveLabels)) {
    return { asked: true, because: "button_label" };
  }
  if (sharesWord(content, vocabulary.stepNames.flatMap((name) => contentWords(tokenize(name))))) {
    return { asked: true, because: "step_name" };
  }

  if (kind === "trigger") {
    const labelWords = vocabulary.buttonLabels.flatMap((label) => contentWords(tokenize(label)));
    if (sharesWord(content, labelWords)) return { asked: true, because: "button_words" };
    if (containsAny(tokens, K_AFFIRMATIVE_PHRASES) || containsAny(tokens, K_NEGATIVE_PHRASES)) {
      return { asked: true, because: "answer_words" };
    }
    return { asked: false, pureNoise: false };
  }

  if (containsAny(tokens, K_MOVE_PHRASES) || K_STEP_REFERENCE.test(` ${tokens.join(" ")} `)) {
    return { asked: true, because: "move_words" };
  }
  return { asked: false, pureNoise: false };
}

// ---------------------------------------------------------------------------

function containsAny(tokens: readonly string[], phrases: readonly string[]): boolean {
  const padded = ` ${tokens.join(" ")} `;
  return phrases.some((phrase) => padded.includes(` ${phrase} `));
}

/** Each label in the tokenized form {@link containsAny} matches: "Let's go!" → "lets go". */
function labelPhrases(labels: readonly string[]): string[] {
  return labels.map((label) => tokenize(label).join(" ")).filter((phrase) => phrase.length > 0);
}

/**
 * Whether the turn says one of the step's own button labels: the whole label
 * ("Let's go", whose words are all too common to count alone), or one of its
 * content words ("Start" in "Okay, start it").
 */
function saysLabel(
  tokens: readonly string[],
  content: readonly string[],
  labels: readonly string[],
): boolean {
  if (labels.length === 0) return false;
  if (containsAny(tokens, labelPhrases(labels))) return true;
  return sharesWord(content, labels.flatMap((label) => contentWords(tokenize(label))));
}

/** Tokens that can say what a step or button is about: 3+ letters, or a number. */
function contentWords(tokens: readonly string[]): string[] {
  return tokens.filter(
    (token) =>
      (token.length >= 3 || /^\d+$/.test(token)) &&
      !K_STOPWORDS.has(token) &&
      !K_FILLER_TOKEN.test(token),
  );
}

function sharesWord(a: readonly string[], b: readonly string[]): boolean {
  return a.some((x) => b.some((y) => sameWord(x, y)));
}

/** Equal, or equal but for a plural ending — "drone" / "drones", "box" / "boxes". */
function sameWord(a: string, b: string): boolean {
  return a === b || a === `${b}s` || b === `${a}s` || a === `${b}es` || b === `${a}es`;
}

/**
 * The last question that asks something. A trailing question that only names
 * the ANSWER FORMAT — "Yes or no?", "Right?", "OK?" — is skipped for the one
 * in front of it, because that is the question the "yes" answers.
 *
 * Spark tacks one on unprompted ("Do you want me to press Next and move to the
 * next step? Yes or no?"), and reading the tail as the question hid the move
 * question: the learner's "Yes." was refused as `move_not_requested`, Spark
 * asked again, and the learner was stuck answering "yes" until they happened to
 * say the button's name (traces of 2026-10-01). A tail with words of its own —
 * "Or is there anything else?" — is still a question, and still the one read.
 */
function lastQuestion(line: string): string | null {
  const questions = line
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.endsWith("?"));
  for (let i = questions.length - 1; i >= 0; i--) {
    if (!isAnswerFormatQuestion(questions[i]!)) return questions[i]!;
  }
  return questions.length > 0 ? questions[questions.length - 1]! : null;
}

/** Words that can only name how to answer, never what is being asked. */
const K_ANSWER_FORMAT_TOKENS: ReadonlySet<string> = new Set([
  "yes", "no", "or", "ok", "okay", "right", "correct", "alright", "yeah", "yep", "nope",
]);

/** "Yes or no?", "Right?", "OK?" — a question made only of answer-format words. */
function isAnswerFormatQuestion(question: string): boolean {
  const tokens = tokenize(question);
  return tokens.length > 0 && tokens.every((token) => K_ANSWER_FORMAT_TOKENS.has(token));
}
